import React from 'react';
import { getContext, getDuration, getEasing } from 'common';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_SHARP } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { useTrail, animated } from 'react-spring';
import { LaunchPadContext } from './LaunchPad';
import { handleKeyDown } from './keyboardNavigation';
import Styled from './styles';

export interface LaunchPadContentProps extends React.ComponentPropsWithRef<'div'> {
  /** Place content here. */
  children: React.ReactNode;
  /** Control animation config here, used in conjunction with `animateIn` prop of Launchpad. This uses React-spring config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** @ignore */
  style?: React.CSSProperties
}

/** Place main login screen content here (eg. forms) */
const LaunchPadContent = React.forwardRef<HTMLElement, LaunchPadContentProps>((props: LaunchPadContentProps, ref) =>
  getContext(({ RTL }) => {
    const { children, animationProps, ...otherProps } = props;
    const contentRef = React.useRef(null);
    const { animateIn, disableAnimation } = React.useContext(LaunchPadContext);
    const trailSprings = useTrail(React.Children.toArray(children).length, animationProps || {
      from: { opacity: 0, transform: 'scale(0.3) perspective(1px)', willChange: 'transform' },
      to: { opacity: 1, transform: 'scale(1)  perspective(1px)', willChange: 'transform' },
      delay: 1500,
      config: {
        duration: getDuration(SPEED_SLOW) * 3,
        easing: getEasing(TRANSITION_SHARP)
      },
      immediate: disableAnimation,
    });
    return (
      <Styled.LaunchPadContent $rtl={RTL} ref={ref} {...otherProps}>
        <Styled.LaunchPadContentInner
          ref={contentRef}
          onKeyDown={handleKeyDown(contentRef, RTL)}
        >
          {animateIn
            ? trailSprings.map((spring, index) => (
              <animated.div
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                style={spring as React.CSSProperties}
              >
                {children[index]}
              </animated.div>
            ))
            : children}
        </Styled.LaunchPadContentInner>
      </Styled.LaunchPadContent>
    );
  }));

LaunchPadContent.displayName = 'LaunchPadContent';
LaunchPadContent.defaultProps = {
  animationProps: undefined,
  style: {}
};

/* Exports */
export { LaunchPadContent as default };
